import {
  Briefcase,
  Calendar,
  Home,
  LayoutGrid,
  MapPin,
  Star,
  Wallet,
} from "lucide-react";
import {
  formatEmploymentDisplay,
  formatSeniorityDisplay,
  getJobCardSalaryLabel,
} from "../pages/applicantData";

const PILL_ICONS = {
  "Advertised on": Calendar,
  Salary: Wallet,
  Location: MapPin,
  Seniority: Star,
  Employment: Briefcase,
  "Job functions": LayoutGrid,
  "Work mode": Home,
};

function Pill({ label, value, className = "" }) {
  const display = value != null && String(value).trim() !== "" ? String(value).trim() : "—";
  return (
    <span className={`job-highlight-pill${className ? ` ${className}` : ""}`}>
      <span className="job-highlight-pill__label">{label}</span>
      <span className="job-highlight-pill__value">{display}</span>
    </span>
  );
}

function DetailMetaCard({ label, value, cardClass = "nd1-job-meta-card" }) {
  const display = value != null && String(value).trim() !== "" ? String(value).trim() : "—";
  const Icon = PILL_ICONS[label];

  return (
    <div className={cardClass}>
      <span className={`${cardClass}__icon`} aria-hidden="true">
        <Icon size={22} strokeWidth={1.5} />
      </span>
      <span className={`${cardClass}__body`}>
        <span className={`${cardClass}__label`}>{label}</span>
        <span className={`${cardClass}__value`}>{display}</span>
      </span>
    </div>
  );
}

/** Calendar + clock from `publishedAt` only — no fallback so we never imply a publish time. */
function formatAdvertPublicationDateTime(job) {
  const raw = job?.publishedAt ?? job?.published_at;
  if (raw == null || raw === "") return "";
  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function JobDetailMetaCards({
  job,
  cardsClass = "nd1-job-meta-cards",
  cardClass = "nd1-job-meta-card",
  compact = false,
  showAdvertisedOn = true,
}) {
  const salary = getJobCardSalaryLabel(job);
  const advertisedOn = formatAdvertPublicationDateTime(job);
  const functions = job.functionalArea || job.department || "";

  if (compact) {
    return (
      <div className={cardsClass}>
        {showAdvertisedOn && advertisedOn ? (
          <DetailMetaCard label="Advertised on" value={advertisedOn} cardClass={cardClass} />
        ) : null}
        <DetailMetaCard label="Salary" value={salary} cardClass={cardClass} />
        <DetailMetaCard label="Location" value={job.location} cardClass={cardClass} />
      </div>
    );
  }

  return (
    <div className={cardsClass}>
      {showAdvertisedOn && advertisedOn ? (
        <DetailMetaCard label="Advertised on" value={advertisedOn} cardClass={cardClass} />
      ) : null}
      <DetailMetaCard label="Salary" value={salary} cardClass={cardClass} />
      <DetailMetaCard label="Location" value={job.location} cardClass={cardClass} />
      <DetailMetaCard label="Seniority" value={formatSeniorityDisplay(job.seniorityLevel)} cardClass={cardClass} />
      <DetailMetaCard label="Employment" value={formatEmploymentDisplay(job.employmentType)} cardClass={cardClass} />
      <DetailMetaCard label="Job functions" value={functions} cardClass={cardClass} />
      <DetailMetaCard label="Work mode" value={job.workMode} cardClass={cardClass} />
    </div>
  );
}

export default function JobHighlightPills({ job, variant = "default", showAdvertisedOn = true }) {
  if (!job) return null;
  const salary = getJobCardSalaryLabel(job);
  const advertisedOn = formatAdvertPublicationDateTime(job);

  if (variant === "detail") {
    return <JobDetailMetaCards job={job} showAdvertisedOn={showAdvertisedOn} />;
  }

  if (variant === "applicant-home") {
    return (
      <JobDetailMetaCards
        job={job}
        cardsClass="nd1-applicant-job-meta-cards"
        cardClass="nd1-applicant-job-meta-card"
        compact
      />
    );
  }

  if (variant === "job-search") {
    return (
      <JobDetailMetaCards
        job={job}
        cardsClass="nd1-applicant-job-search-meta-cards"
        cardClass="nd1-applicant-job-search-meta-card"
      />
    );
  }

  return (
    <div className="job-highlights">
      {advertisedOn ? <Pill label="Advertised on" value={advertisedOn} /> : null}
      <Pill label="Salary" value={salary} />
      <Pill label="Location" value={job.location} />
      <Pill label="Seniority" value={formatSeniorityDisplay(job.seniorityLevel)} />
      <Pill label="Employment" value={formatEmploymentDisplay(job.employmentType)} />
      <Pill label="JOB FUNCTIONS" value={job.functionalArea || job.department} />
      <Pill label="Work mode" value={job.workMode} />
    </div>
  );
}
